'use client';

import React, { useEffect, useRef, useState } from 'react';
import SwiperCore from 'swiper';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CaretCircleRight, CaretCircleLeft } from '@phosphor-icons/react';

import 'swiper/css';
import 'swiper/css/bundle';
import { SectionItem } from '@/types/product_page/productType';
import styles from './slideshow.module.css';
import { generateDirectusSrc, generateDirectusSrcSet } from '@/helpers/image';

SwiperCore.use([Pagination, Autoplay, Navigation]);

export type carouselItem = {
  image: string;
};

const SlideShow = ({ data }: { data: SectionItem }) => {
  const swiperRef = useRef<any>(null);
  const [opacity, setOpacity] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const handleNextButtonClick = () => {
    setOpacity(true);
    setTimeout(() => {
      setOpacity(false);
      swiperRef.current.slideNext();
    }, 150);
  };

  // Custom previous button click handler
  const handlePrevButtonClick = () => {
    setOpacity(true);
    setTimeout(() => {
      setOpacity(false);
      swiperRef.current.slidePrev();
    }, 150);
  };

  const [isFilled, setIsFilled] = useState({ 1: false, 2: false });
  const [activeIndex, setActiveIndex] = useState(0); // Track the active slide index

  // Update activeIndex state when the slide changes
  const onSlideChange = (swiper: any) => {
    setActiveIndex(swiper.realIndex % data.slides.length);
  };

  // Navigate to a specific slide
  const navigateToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
  };

  const duplicateSlides = (slides: any[], minSlides: number) => {
    let duplicatedSlides = [...slides];
    while (duplicatedSlides.length < minSlides) {
      duplicatedSlides = duplicatedSlides.concat(slides);
    }
    return duplicatedSlides.slice(0, minSlides);
  };

  const originalSlides =
    isMobile && data.mobile_slides?.length > 0
      ? data.mobile_slides
      : data.slides;

  const slidesToDisplay = duplicateSlides(
    isMobile && data.mobile_slides?.length > 0
      ? data.mobile_slides
      : data.slides,
    6
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      style={{
        background: data.bg_color || 'black',
        color: data.text_color || 'white',
      }}
      className="overflow-hidden py-8 md:py-14"
      id={data?.heading}
    >
      <h1 className={`text-3xl lg:text-4.5xl font-bold text-center mb-8`}>
        {data.heading}
      </h1>
      <p className="font-medium font-proxima_font text-center max-w-screen-md px-4 mx-auto mb-8 opacity-80">
        {data.description}
      </p>
      <div className={`w-full h-full px-4 relative `}>
        <Swiper
          effect="slide"
          direction="horizontal"
          slidesPerView={1}
          spaceBetween={20}
          centeredSlides={true}
          // navigation={true}
          initialSlide={0}
          // pagination={{
          //   clickable: true,
          // }}
          modules={[Pagination]}
          watchOverflow={true}
          loop={true}
          speed={700}
          className={`${styles.swiper_container3} max-w-[1920px] !overflow-visible`}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={{
            1024: {
              slidesPerView: 1.5,
            },
          }}
          onSlideChange={onSlideChange}
        >
          {slidesToDisplay?.map((image, index) => (
            <SwiperSlide
              key={index}
              className={`md:rounded-xl overflow-hidden`}
            >
              <div
                className={`relative h-full md:h-96 ${
                  swiperRef?.current?.realIndex == index && opacity
                    ? 'opacity-50'
                    : ''
                } transition-opacity duration-300 opacity-100 lg:h-[650px]`}
              >
                <img
                  src={generateDirectusSrc(image.directus_files_id, 1200)}
                  srcSet={generateDirectusSrcSet(image.directus_files_id)}
                  sizes="(max-width: 768px) 100vw, 1200px"
                  alt="image"
                  className="w-full h-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </SwiperSlide>
          ))}
          <div className="flex items-center justify-between lg:max-w-[66.5%] mx-auto mt-5">
            <div className="flex items-center gap-2">
              {originalSlides?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => navigateToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  style={{ background: data.text_color || '#fff' }}
                  className={`size-[10px] rounded-full outline-none appearance-none ${index === activeIndex ? 'opacity-100' : 'opacity-10'}`}
                ></button>
              ))}
            </div>

            <div className="flex items-center justify-end w-full">
              <button
                onMouseEnter={() => {
                  setIsFilled({ 1: true, 2: false });
                }}
                onMouseLeave={() => {
                  setIsFilled({ 1: false, 2: false });
                }}
                onClick={() => {
                  handlePrevButtonClick();
                }}
                className="rounded-full overflow-hidden bg-transparent  flex items-center justify-center"
              >
                <CaretCircleLeft
                  size={40}
                  weight={isFilled[1] ? 'fill' : 'regular'}
                />
              </button>

              <button
                onMouseEnter={() => {
                  setIsFilled({ 1: false, 2: true });
                }}
                onMouseLeave={() => {
                  setIsFilled({ 1: false, 2: false });
                }}
                onClick={() => {
                  handleNextButtonClick();
                }}
                className="rounded-full overflow-hidden  bg-transparent  flex items-center justify-center"
              >
                <CaretCircleRight
                  size={40}
                  weight={isFilled[2] ? 'fill' : 'regular'}
                />
              </button>
            </div>
          </div>
        </Swiper>
      </div>
    </div>
  );
};

export default SlideShow;
